import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {  Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, } from "reactstrap";
import { useQueryRetrievePlannedSprays, useQueryRetrieveExecutedSprays, } from "../../../services/stores";
import { SprayCampaign, SprayExecuted, SprayProduct, blankSprayCampaign, blankSprayExecuted } from "../../../types";
import { SprayListFilter } from "./SprayListFilter";
import "../../../assets/css/tanseeq.css";
import toast, { Toaster } from "react-hot-toast";
import { formatDateDDMMM, formatDateYYYYMMDD } from "../../../helpers/utils";
import styled from 'styled-components';
import { ErrorBanner } from "../../../components/ErrorBanner";
import { ApplicationsCalendar, combineDateClassNames } from "../../../components/ApplicationsCalendar";
import LoadingInPogress from "../../../components/LoadingInProgress";
import { SprayCard } from "./SprayCard";
import { SprayExecutedCard } from "./SprayExecutedCard";
import { Carousel } from 'react-bootstrap';

type CalendarValue = Date | Date[] | null | [Date | null, Date | null];


const ProductCardsContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 15px;
`;


export const SprayPlannedList = () => {
  const navigate = useNavigate();

  const [filterPlannedSprays, setFilterPlannedSprays] = useState<"NONE" | "RELEASED" | "NOT RELEASED">("RELEASED");
  const [filteredPlannedSprays, setFilteredPlannedSprays] = useState<SprayCampaign[] | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmCalendar, setShowConfirmCalendar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState<Date>(selectedDate);

  const [selectedPlannedSprayID, setSelectedPlannedSprayID] = useState<string>("");
  const [selectedExecutedSprayID, setSelectedExecutedTreatmentID] = useState<string>("");
  const [selectedPlannedSpray, setSelectedPlannedSpray] = useState<SprayCampaign>(blankSprayCampaign);
  const [selectedExecutedSpray, setSelectedExecutedTreatment] = useState<SprayExecuted>(blankSprayExecuted);

  const { data: plannedSprays, 
      isLoading : isLoadingPlannedSprays, 
      error: queryErrorPlannedSprays } = useQueryRetrievePlannedSprays();

  const { data: rawExecutedSprays, 
      isLoading : isLoadingExecutedSprays, 
      error: queryErrorExecutedSprays } = useQueryRetrieveExecutedSprays();


  // Filter planned sprays
  //
  useEffect(() => {
    if (plannedSprays) {
      setFilteredPlannedSprays(
        filterPlannedSprays === "NONE"
          ? plannedSprays
          : plannedSprays.filter(spray => (filterPlannedSprays === "RELEASED" ? spray.releasedForExecution : !spray.releasedForExecution))
      );
    } else {
      setFilteredPlannedSprays([]);
    }
  }, [plannedSprays, filterPlannedSprays]);

  // Get class names for dates with planned applications
  //
  const plannedDateClassNames = useMemo(() => {
    return (filteredPlannedSprays || []).reduce((acc, spray) => {
      const dateStr = formatDateYYYYMMDD(spray.plannedStartDate) || '';
      if (!acc[dateStr]) {
          acc[dateStr] = [];
      }
      if (!acc[dateStr].includes('has-planned-applications')) {  
        acc[dateStr].push('has-planned-applications');
      }
      return acc;
    }, {} as Record<string, string[]>);

  }, [filteredPlannedSprays]);

  const executedDateClassNames = useMemo(() => {
    return (rawExecutedSprays || []).reduce((acc, spray) => {
      const dateStr = formatDateYYYYMMDD(spray.startDate) || '';
      if (!acc[dateStr]) {
          acc[dateStr] = [];
      }
      if (!acc[dateStr].includes('has-executed-applications')) {
        acc[dateStr].push('has-executed-applications');
      }
      return acc;
    }, {} as Record<string, string[]>);

  }, [rawExecutedSprays]);

  const dateClassNames = useMemo(() => {
    return combineDateClassNames(plannedDateClassNames, executedDateClassNames);
  }, [plannedDateClassNames, executedDateClassNames]);  

  
  const toggleConfirmCalendar = () => {
    setShowConfirmCalendar(!showConfirmCalendar);
  };

  // Find applications for selected date
  //
  const selectedPlannedApplications = useMemo(() => {  
    return filteredPlannedSprays?.filter(
      spray => new Date(spray.plannedStartDate).toDateString() === selectedDate.toDateString()
    ) || [];
  }, [filteredPlannedSprays, selectedDate]);

  useEffect(() => {
    if (selectedPlannedApplications.length >= 1) {
      const sprayId = selectedPlannedApplications[0].id;
      setSelectedPlannedSprayID(sprayId);
      setSelectedPlannedSpray(plannedSprays?.find(spray => spray.id === sprayId) || blankSprayCampaign);
    }
  }, [filteredPlannedSprays, selectedDate]);  
 
  const selectedDateExecutedSprays = rawExecutedSprays?.filter(
    spray => new Date(spray.startDate).toDateString() === selectedDate.toDateString()
  ) || [];

  const executedSpraysByTreatmentID = useMemo(() => {
    return selectedDateExecutedSprays?.reduce((acc, spray) => {
      if (!acc[spray.treatmentID]) {
          acc[spray.treatmentID] = [];
    }
    acc[spray.treatmentID].push(spray);
      return acc;
    }, {} as Record<string, SprayExecuted[]>);
  }, [selectedDateExecutedSprays]);

  const acknowledgedSprayCountByTreatmentID = useMemo(() => {
    return selectedDateExecutedSprays.reduce((acc, spray) => {
      acc[spray.treatmentID] = (acc[spray.treatmentID] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [selectedDateExecutedSprays]);


  const onConfirmSprayComplete = async () => {
    try {
      // Implementation for spray completion
      setShowConfirmModal(false);
    } catch (error) {
      setError("Spray déjà réalisé");
    }
  };

  const promptUserForSprayConfirmation = () => {
    setShowConfirmModal(true);
  };

  const handleApplicationDateChange = (value: CalendarValue) => {
    if (value instanceof Date) {
      setCompletionDate(value);
    }
  };


  if (isLoadingPlannedSprays || isLoadingExecutedSprays) {
    return (
      <LoadingInPogress message="Chargement de la liste des traitements..." />
    );
  }
  
  if (queryErrorPlannedSprays || queryErrorExecutedSprays) {
    return <div>Erreur durant le chargement des informations: {queryErrorPlannedSprays?.message || queryErrorExecutedSprays?.message}</div>;
  }



  let dateFirstPlannedSpray = new Date("2099-12-31");
  selectedPlannedApplications.forEach(spray => {
    if (spray.releasedForExecution && new Date(spray.plannedStartDate) < dateFirstPlannedSpray) {
      dateFirstPlannedSpray = new Date(spray.plannedStartDate);
    }
  });

  const hasPlannedSprays = (selectedPlannedApplications.length > 0);
  const hasExecutedSprays = (Object.entries(executedSpraysByTreatmentID).length > 0);
  const hasMultipleSprays = ((selectedPlannedApplications.length + Object.entries(executedSpraysByTreatmentID).length) > 1);

  return (
    <div className="spray-planned">
      <Toaster />

      <div style={{ marginBottom: '10px' }} >
        <SprayListFilter 
          filter={filterPlannedSprays} 
          setFilter={setFilterPlannedSprays}
        />
      </div>

      <ApplicationsCalendar
        dateClassNames={dateClassNames}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />

      {
        selectedPlannedApplications.length < 1 && selectedDateExecutedSprays.length < 1 && ( 
          <div className="d-flex justify-content-center align-items-center spray-planned product-cards mt-4">
            Pas de traitement programmé ou réalisé le {formatDateDDMMM(selectedDate)}!
          </div>
        )
      }


    {(hasPlannedSprays || hasExecutedSprays) &&
        <Carousel
        interval={null} 
        className="mt-3"      
        indicators={hasMultipleSprays}
        controls={hasMultipleSprays}
        >
          {selectedPlannedApplications.map((spray, index) => (
            <Carousel.Item 
              key={spray.id} 
              className="mt-4"
              onClick={(e) => {e.stopPropagation();}}
            >
              <SprayCard 
                  key={spray.id + 'SPRAY-CARD'} 
                  spray={spray} 
                  dateFirstPlannedSpray={dateFirstPlannedSpray} 
                  index={index}
                  count={selectedPlannedApplications.length}
                  acknowledgedSprayCount={acknowledgedSprayCountByTreatmentID[spray.id] || 0}
              />
            </Carousel.Item>
          ))}

          {Object.entries(executedSpraysByTreatmentID).map(([treatmentID, sprays], index) => (
            <Carousel.Item 
              key={treatmentID}  
              className="mt-4"
              onClick={(e) => {e.stopPropagation();}}
            >
              <div 
                className="d-flex justify-content-center"
                onClick={(e) => {e.stopPropagation();}}  
              >
                <SprayExecutedCard 
                  key={`spray-card-${treatmentID}`} 
                  treatmentID={treatmentID}
                  sprays={sprays} 
                  index={index}
                count={Object.entries(executedSpraysByTreatmentID).length}
              />
            </div>
            </Carousel.Item>
          )) }
        
        </Carousel>
        }


      <ProductCardsContainer>
        {error && (
          <ErrorBanner>
            {error}
          </ErrorBanner>
        )}

      </ProductCardsContainer>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          Confirmer la réalisation du spray
        </ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <label className="form-label">Date de réalisation</label>
            <Input
              type="date"
              value={formatDateYYYYMMDD(selectedDate)}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="form-control"
            />
          </div>
          
          <div className="applications-list">
            {/* Map through selected applications */}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
            Annuler
          </Button>
          <Button color="success" onClick={onConfirmSprayComplete}>
            Confirmer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

/*
      <div className="spray-planned product-cards" style={{ marginTop: '10px' }}>

            <SprayListTable 
              sprays={plannedSprays} 
              toProductName={toProductName} 
              onEdit={handleEdit} 
              onExecute={handleExecute} 
            />
      </div>




      
      {Object.entries(executedSpraysByTreatmentID).length > 0 && 
          <Carousel 
            interval={null}
            indicators={Object.entries(executedSpraysByTreatmentID).length > 1}
            controls={Object.entries(executedSpraysByTreatmentID).length > 1}
            className="mt-3"
            defaultActiveIndex={0}
            wrap={false}
            slide={true}
          >
            {Object.entries(executedSpraysByTreatmentID).map(([treatmentID, sprays], index) => {
              console.log('Rendering item:', index, treatmentID);
              return (
                <Carousel.Item key={`carousel-item-${treatmentID}`}>
                  <div className="d-flex justify-content-center">
                    <SprayExecutedCard 
                      key={`spray-card-${treatmentID}`}
                      treatmentID={treatmentID}
                      sprays={sprays} 
                      onUserConfirmsEdit={() => {}}
                      index={index}
                      count={Object.entries(executedSpraysByTreatmentID).length}
                    />
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
      }


  */